<template>
  <div class="crm-status-indicator" :class="statusClass">
    <div class="status-icon">
      <img src="https://www.zohowebstatic.com/sites/zweb/images/commonroot/zoho-logo-web.svg"
           alt="Zoho CRM"
           class="crm-logo" />
      <div v-if="status.connected && !status.token_expired" class="status-dot connected"></div>
      <div v-else-if="status.token_expired" class="status-dot warning"></div>
      <div v-else class="status-dot disconnected"></div>
    </div>
    <div class="status-text">
      <span class="status-label">CRM</span>
      <span class="status-value">{{ statusText }}</span>
    </div>
    <button v-if="showSyncButton && canSync"
            @click="$emit('sync')"
            :disabled="syncing"
            class="sync-button">
      <span v-if="syncing" class="sync-spinner"></span>
      <span v-else>↻</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'CRMStatusIndicator',
  props: {
    status: {
      type: Object,
      default: () => ({
        connected: false,
        token_expired: false,
        sync_enabled: false
      })
    },
    showSyncButton: {
      type: Boolean,
      default: false
    },
    syncing: {
      type: Boolean,
      default: false
    }
  },
  emits: ['sync'],
  computed: {
    statusText () {
      if (this.status.token_expired) return 'Expired'
      return this.status.connected ? 'Connected' : 'Not Connected'
    },
    statusClass () {
      if (this.status.token_expired) return 'warning'
      return this.status.connected ? 'connected' : 'disconnected'
    },
    canSync () {
      return this.status.connected && !this.status.token_expired && this.status.sync_enabled
    }
  }
}
</script>

<style scoped>
.crm-status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-card);
  transition: all 0.2s ease;
}

.crm-status-indicator.connected {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.crm-status-indicator.warning {
  border-color: #f59e0b;
  background: rgba(245, 158, 11, 0.05);
}

.crm-status-indicator.disconnected {
  border-color: var(--border-secondary);
  background: var(--bg-secondary);
}

.status-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.crm-logo {
  width: 20px;
  height: auto;
  opacity: 0.8;
}

.status-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid var(--bg-card);
}

.status-dot.connected {
  background: #10b981;
}

.status-dot.warning {
  background: #f59e0b;
}

.status-dot.disconnected {
  background: #6b7280;
}

.status-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.status-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-value {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
}

.crm-status-indicator.connected .status-value {
  color: #10b981;
}

.crm-status-indicator.warning .status-value {
  color: #f59e0b;
}

.sync-button {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
}

.sync-button:hover:not(:disabled) {
  background: var(--bg-secondary);
  color: var(--text-primary);
  transform: rotate(180deg);
}

.sync-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sync-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid var(--text-tertiary);
  border-radius: 50%;
  border-top-color: var(--text-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Compact variant */
.crm-status-indicator.compact {
  padding: 4px 8px;
  gap: 6px;
}

.crm-status-indicator.compact .crm-logo {
  width: 16px;
}

.crm-status-indicator.compact .status-text {
  display: none;
}

.crm-status-indicator.compact .sync-button {
  width: 20px;
  height: 20px;
  font-size: 10px;
}
</style>
